import type {
  AppendRoomEventsResponse,
  RoomEvent,
  RoomEventsResponse,
} from '@ai-meeting/shared'
import { apiClient, toApiError } from './api'
import { getServiceStrings } from './i18n'

/**
 * Everything a shared room's socket said before now (BRAI-539).
 *
 * @param after A `cursor` from an earlier reply, to read only what the room has
 *   been told since. Omitted on the read a reader does when it arrives; passed
 *   on the one it does a few seconds after its own socket goes live, which is
 *   what finds a line that fell between the two.
 * @throws ApiError, which the caller is expected to swallow: a late joiner
 *   with no history is worse off than one with it, but a meeting that refuses
 *   to start is worse than both.
 */
export async function fetchRoomEvents(
  sharedRoomId: string,
  after?: string
): Promise<RoomEventsResponse> {
  try {
    const { data } = await apiClient.get<RoomEventsResponse>(
      `/api/rooms/${sharedRoomId}/events`,
      after === undefined ? undefined : { params: { after } }
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedLoadRoomHistory)
  }
}

/**
 * Report frames heard just now, duplicates and all.
 *
 * Every participant posts the same frames: the server keeps the first copy of
 * each, so nobody has to know who else is listening, and the history survives
 * whoever closes their tab first.
 *
 * @throws ApiError — also meant to be swallowed. Losing a batch costs a late
 *   joiner a few lines; interrupting the meeting to say so costs more.
 */
export async function appendRoomEvents(
  sharedRoomId: string,
  events: RoomEvent[]
): Promise<AppendRoomEventsResponse> {
  try {
    const { data } = await apiClient.post<AppendRoomEventsResponse>(
      `/api/rooms/${sharedRoomId}/events`,
      { events }
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedSaveRoomHistory)
  }
}
